'use client';

import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

interface SessionLoadingGuardProps {
  children: React.ReactNode;
  session?: any;
}

/**
 * A component that ensures the session is fully loaded before rendering children
 * This helps prevent race conditions where components try to access session data
 * before it's fully hydrated after login
 */
export default function SessionLoadingGuard({
  children,
  session: ssrSession,
}: SessionLoadingGuardProps) {
  const pathname = usePathname();

  const isSessionComplete = (sess: any) => {
    if (!sess?.user) return false;
    const user = sess.user;
    return (
      !!user.id &&
      (user.isSourcer !== undefined || user.isShopper !== undefined)
    );
  };

  // If we have an SSR session and it's complete, skip client hook entirely
  if (ssrSession && isSessionComplete(ssrSession)) {
    return <>{children}</>;
  }

  // Otherwise, fall back to a client guard that uses useSession
  return (
    <ClientSessionGuard pathname={pathname}>{children}</ClientSessionGuard>
  );
}

function ClientSessionGuard({
  children,
  pathname,
}: {
  children: React.ReactNode;
  pathname: string | null;
}) {
  const { data: session, status } = useSession();
  const [isSessionReady, setIsSessionReady] = useState(false);

  useEffect(() => {
    // Public paths render immediately
    if (
      pathname === '/' ||
      pathname === '/login' ||
      pathname === '/register' ||
      pathname === '/privacy-policy' ||
      pathname === '/unsubscribe' ||
      pathname === '/user-type' ||
      pathname?.startsWith('/engage') ||
      pathname?.startsWith('/marketing') ||
      pathname?.includes('unsubscribe')
    ) {
      setIsSessionReady(true);
      return;
    }

    if (status === 'unauthenticated') {
      const protectedPaths = [
        '/orders',
        '/sourcer',
        '/shopper',
        '/clients',
        '/settings',
        '/notifications',
      ];
      const isProtectedPath = protectedPaths.some(
        (path) => pathname === path || pathname?.startsWith(`${path}/`)
      );
      if (isProtectedPath) {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            'Unauthenticated on protected path, redirecting to login:',
            pathname
          );
        }
        window.location.href = '/login';
        return;
      }
      setIsSessionReady(true);
      return;
    }

    if (status === 'loading') return;

    if (status === 'authenticated') {
      setIsSessionReady(true);
      return;
    }
  }, [status, session?.user, pathname]);

  if (!isSessionReady && status === 'authenticated') {
    if (process.env.NODE_ENV === 'development') {
      console.log('Loading session data...');
    }
    return (
      <div className="fixed inset-0 z-50 flex justify-center items-center bg-white bg-opacity-90">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-sourced-black mx-auto"></div>
          <p className="mt-4 text-lg font-medium">Loading session data...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
